import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { AiOutlineCloudUpload, AiOutlineClose } from "react-icons/ai";
import { BiImageAdd, BiVideoPlus } from "react-icons/bi";
import { toast } from "react-toastify";
import ProductAttributesForm from "../Shop/ProductAttributesForm";
import CategoryTreeSelect from "../common/CategoryTreeSelect";

const PRODUCT_TYPES = ["Herbal", "Handmade", "Organic", "Wellness", "Traditional", "Lifestyle", "Other"];

const ProductFormModal = ({
  open,
  onClose,
  product,
  onSubmit,
  isEdit = false,
  loading = false,
  categories = [],
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    shortDescription: "",
    category: "",
    tags: "",
    price: "",
    discountedPrice: "",
    costPrice: "",
    stock: "",
    weight: "",
    productType: "Herbal",
    stateOrigin: "",
    ingredients: "",
    usageInstructions: "",
    benefits: "",
    isFeatured: false,
    isBestSeller: false,
    seoTitle: "",
    seoDescription: "",
    metaKeywords: "",
  });

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [attributes, setAttributes] = useState([]);

  useEffect(() => {
    if (isEdit && product) {
      const categoryId = product.category?._id || product.category || "";
      setFormData({
        name: product.name || "",
        description: product.description || "",
        shortDescription: product.shortDescription || "",
        category: categoryId,
        tags: Array.isArray(product.tags) ? product.tags.join(", ") : (product.tags || ""),
        price: product.price || "",
        discountedPrice: product.discountedPrice || "",
        costPrice: product.costPrice || "",
        stock: product.stock || "",
        weight: product.weight || "",
        productType: product.productType || "Herbal",
        stateOrigin: product.stateOrigin || "",
        ingredients: Array.isArray(product.ingredients) ? product.ingredients.join(", ") : (product.ingredients || ""),
        usageInstructions: product.usageInstructions || "",
        benefits: Array.isArray(product.benefits) ? product.benefits.join(", ") : (product.benefits || ""),
        isFeatured: product.isFeatured || false,
        isBestSeller: product.isBestSeller || false,
        seoTitle: product.seoTitle || "",
        seoDescription: product.seoDescription || "",
        metaKeywords: Array.isArray(product.metaKeywords) ? product.metaKeywords.join(", ") : (product.metaKeywords || ""),
      });

      if (product.images && product.images.length > 0) {
        setImagePreviews(product.images.map((img) => img.url));
      }
      if (product.videos && product.videos.length > 0) {
        setVideoPreviews(product.videos.map((video) => video.url));
      }
      if (product.attributes && product.attributes.length > 0) {
        setAttributes(product.attributes);
      } else {
        setAttributes([]);
      }
    } else {
      resetForm();
    }
  }, [product, isEdit, open]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      shortDescription: "",
      category: "",
      tags: "",
      price: "",
      discountedPrice: "",
      costPrice: "",
      stock: "",
      weight: "",
      productType: "Herbal",
      stateOrigin: "",
      ingredients: "",
      usageInstructions: "",
      benefits: "",
      isFeatured: false,
      isBestSeller: false,
      seoTitle: "",
      seoDescription: "",
      metaKeywords: "",
    });
    setImages([]);
    setImagePreviews([]);
    setVideos([]);
    setVideoPreviews([]);
    setAttributes([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategoryChange = (categoryObj) => {
    setFormData((prev) => ({
      ...prev,
      category: categoryObj ? categoryObj._id : "",
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const invalidFiles = files.filter((f) => !validTypes.includes(f.type));
    if (invalidFiles.length > 0) { toast.error("Please select only image files (JPEG, PNG, WebP)"); return; }
    const maxSize = 5 * 1024 * 1024;
    if (files.some((f) => f.size > maxSize)) { toast.error("Please select images smaller than 5MB"); return; }
    setImages(files);
    Promise.all(files.map((f) => new Promise((r) => { const reader = new FileReader(); reader.onload = (e) => r(e.target.result); reader.readAsDataURL(f); }))).then(setImagePreviews);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const validTypes = ["video/mp4", "video/webm", "video/ogg"];
    if (files.some((f) => !validTypes.includes(f.type))) { toast.error("Please select only video files"); return; }
    if (files.some((f) => f.size > 50 * 1024 * 1024)) { toast.error("Please select videos smaller than 50MB"); return; }
    setVideos(files);
    Promise.all(files.map((f) => new Promise((r) => { const reader = new FileReader(); reader.onload = (e) => r(e.target.result); reader.readAsDataURL(f); }))).then(setVideoPreviews);
  };

  const removeVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
    setVideoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Please enter product name"); return; }
    if (!formData.description.trim()) { toast.error("Please enter product description"); return; }
    if (!formData.category) { toast.error("Please select a category"); return; }
    if (!formData.price || formData.price <= 0) { toast.error("Please enter a valid price"); return; }
    if (formData.discountedPrice && parseFloat(formData.discountedPrice) >= parseFloat(formData.price)) {
      toast.error("Discounted price must be less than price"); return;
    }
    if (!formData.stock || formData.stock < 0) { toast.error("Please enter a valid stock quantity"); return; }

    const submitData = new FormData();
    Object.keys(formData).forEach((key) => { submitData.append(key, formData[key]); });
    images.forEach((image) => { submitData.append("images", image); });
    videos.forEach((video) => { submitData.append("videos", video); });
    const validAttributes = attributes.filter((attr) => attr.name?.trim() && attr.values?.some((v) => v.value?.trim()));
    submitData.append("attributes", JSON.stringify(validAttributes));

    try {
      await onSubmit(submitData, product?._id);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Failed to submit form");
    }
  };

  const handleClose = () => { resetForm(); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth aria-labelledby="product-form-title">
      <DialogTitle id="product-form-title" className="flex justify-between items-center">
        <span className="text-xl font-bold">{isEdit ? "Edit Product" : "Create New Product"}</span>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
          <AiOutlineClose size={24} />
        </button>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent className="space-y-6">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange}
              placeholder="Enter product name" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
          </div>

          {/* Short Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
            <input type="text" name="shortDescription" value={formData.shortDescription} onChange={handleInputChange}
              placeholder="Brief description (for cards/previews)" maxLength={200}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange}
              placeholder="Enter detailed product description" rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" required />
          </div>

          {/* Category & Product Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <CategoryTreeSelect categories={categories} value={formData.category} onChange={handleCategoryChange} placeholder="Select a category" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Type *</label>
              <select name="productType" value={formData.productType} onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                {PRODUCT_TYPES.map((type) => (<option key={type} value={type}>{type}</option>))}
              </select>
            </div>
          </div>

          {/* State Origin & Tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State of Origin</label>
              <input type="text" name="stateOrigin" value={formData.stateOrigin} onChange={handleInputChange}
                placeholder="e.g. Kerala, Rajasthan" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input type="text" name="tags" value={formData.tags} onChange={handleInputChange}
                placeholder="Comma separated tags" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Pricing</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (MRP) *</label>
                <input type="number" name="price" value={formData.price} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discounted Price</label>
                <input type="number" name="discountedPrice" value={formData.discountedPrice} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price</label>
                <input type="number" name="costPrice" value={formData.costPrice} onChange={handleInputChange}
                  placeholder="0.00" min="0" step="0.01" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          {/* Stock & Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock *</label>
              <input type="number" name="stock" value={formData.stock} onChange={handleInputChange}
                placeholder="0" min="0" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (grams)</label>
              <input type="number" name="weight" value={formData.weight} onChange={handleInputChange}
                placeholder="0" min="0" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            </div>
          </div>

          {/* Product Details */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="text-sm font-semibold text-green-800 mb-3">Product Details</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ingredients</label>
                <textarea name="ingredients" value={formData.ingredients} onChange={handleInputChange}
                  placeholder="List ingredients (comma separated)" rows={2}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Instructions</label>
                <textarea name="usageInstructions" value={formData.usageInstructions} onChange={handleInputChange}
                  placeholder="How to use this product" rows={2}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
                <textarea name="benefits" value={formData.benefits} onChange={handleInputChange}
                  placeholder="Key benefits (comma separated)" rows={2}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" />
              </div>
            </div>
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleInputChange}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500" />
              <span className="text-sm font-medium text-gray-700">Featured Product</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" name="isBestSeller" checked={formData.isBestSeller} onChange={handleInputChange}
                className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500" />
              <span className="text-sm font-medium text-gray-700">Best Seller</span>
            </label>
          </div>

          {/* SEO */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-800 mb-3">SEO Settings</h4>
            <div className="space-y-3">
              <input type="text" name="seoTitle" value={formData.seoTitle} onChange={handleInputChange}
                placeholder="SEO Title" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <textarea name="seoDescription" value={formData.seoDescription} onChange={handleInputChange}
                placeholder="SEO Description" rows={2}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
              <input type="text" name="metaKeywords" value={formData.metaKeywords} onChange={handleInputChange}
                placeholder="Meta keywords (comma separated)" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
          </div>

          {/* Product Attributes */}
          <ProductAttributesForm attributes={attributes} onChange={setAttributes} />

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
              <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" id="product-images" />
              <label htmlFor="product-images" className="cursor-pointer flex flex-col items-center justify-center">
                <BiImageAdd size={48} className="text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-1">Click to upload product images</p>
                <p className="text-xs text-gray-400">PNG, JPG, WebP up to 5MB each</p>
              </label>
            </div>
            {imagePreviews.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                <div className="grid grid-cols-2 400px:grid-cols-3 800px:grid-cols-4 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                        <AiOutlineClose size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Videos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Videos (Optional)</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
              <input type="file" accept="video/*" multiple onChange={handleVideoChange} className="hidden" id="product-videos" />
              <label htmlFor="product-videos" className="cursor-pointer flex flex-col items-center justify-center">
                <BiVideoPlus size={48} className="text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-1">Click to upload product videos</p>
                <p className="text-xs text-gray-400">MP4, WebM, OGG up to 50MB each</p>
              </label>
            </div>
            {videoPreviews.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Video Preview:</p>
                <div className="grid grid-cols-1 400px:grid-cols-2 gap-4">
                  {videoPreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <video src={preview} className="w-full h-32 object-cover rounded-lg border border-gray-200" controls muted />
                      <button type="button" onClick={() => removeVideo(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                        <AiOutlineClose size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className="px-6 py-4 bg-gray-50">
          <button type="button" onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" disabled={loading}>
            Cancel
          </button>
          <button type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed" disabled={loading}>
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEdit ? "Updating..." : "Creating..."}
              </div>
            ) : isEdit ? "Update Product" : "Create Product"}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProductFormModal;